/**
 * Renders MixBuilderState to the DOM and wires up interactions for the
 * Pick-n-Mix Machine. Physics-based candy drop (Phase 3) and real cart
 * integration (Phase 2) are both live. Seal it adds every bag's items to
 * the real Shopify cart as separate line items (quantity = oz), each
 * tagged with a shared _bag_id/_bag_number property (numbered globally
 * across the whole cart, not reset per session) so the cart display and
 * packing-slip generator can group and separate bags. Candy rows can be
 * dragged between bags (mouse) or moved with the Move button
 * (touch/keyboard).
 */


// Per-candy Halloween effects are hand-drawn in pick-your-mix-fx.js (window.MixFX).
const CARD_ANIMS = {
  shake: [{ transform: 'translateX(0)' }, { transform: 'translateX(-5px) rotate(-1deg)' }, { transform: 'translateX(5px) rotate(1deg)' }, { transform: 'translateX(-4px)' }, { transform: 'translateX(3px)' }, { transform: 'translateX(0)' }],
  wobble: [{ transform: 'rotate(0)' }, { transform: 'rotate(-4deg) scale(1.02)' }, { transform: 'rotate(4deg) scale(1.02)' }, { transform: 'rotate(-3deg)' }, { transform: 'rotate(2deg)' }, { transform: 'rotate(0)' }],
  pulse: [{ transform: 'scale(1)' }, { transform: 'scale(1.05)' }, { transform: 'scale(0.99)' }, { transform: 'scale(1)' }],
  flicker: [{ filter: 'brightness(1)' }, { filter: 'brightness(1.9)' }, { filter: 'brightness(0.8)' }, { filter: 'brightness(1.5)' }, { filter: 'brightness(1)' }],
  jolt: [{ transform: 'translateY(0) scale(1)' }, { transform: 'translateY(-6px) scale(1.04)' }, { transform: 'translateY(2px) scale(0.98)' }, { transform: 'translateY(0) scale(1)' }],
};

// Phones only: plain words instead of "seal" (the desktop machine keeps its wording).
const phoneWords = (t) => {
  if (typeof t !== 'string' || !window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return t;
  return t.replace(/^Sealed! (.*) added to your cart$/, 'Added! $1 is in your cart').replace('Ready to seal', 'Ready to add to cart').replace('more oz to seal.', 'more oz to add to your cart.').replace('before you can seal', 'before you can add to cart').replace('Minimum unlocked! You can seal it', 'Minimum reached! Tap Add to cart').replace('You can seal it', 'You can add it to your cart').replace('then seal it again', 'then add it to your cart again');
};

class MixBuilder extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-mix-candies]');
    let candies = [];
    try {
      candies = JSON.parse(dataEl?.textContent || '[]');
    } catch (e) {
      candies = [];
    }

    const photoUrlsEl = this.querySelector('[data-temp-photo-urls]');
    try {
      this.tempPhotoUrls = JSON.parse(photoUrlsEl?.textContent || '{}');
    } catch (e) {
      this.tempPhotoUrls = {};
    }

    // Brighter, multi-piece composites for the candy grid cards only — kept
    // separate from tempPhotoUrls (physics textures) because a physics piece
    // needs one clean single candy, not a 4-piece collage image.
    const cardPhotoUrlsEl = this.querySelector('[data-card-photo-urls]');
    try {
      this.cardPhotoUrls = JSON.parse(cardPhotoUrlsEl?.textContent || '{}');
    } catch (e) {
      this.cardPhotoUrls = {};
    }

    // Transparent cut-outs by product handle (Ash's rendered photos). These are
    // the physics textures; they win over a product photo, which is a white
    // square and would look wrong falling into the bag.
    const cutoutUrlsEl = this.querySelector('[data-cutout-urls]');
    try {
      this.cutoutUrls = JSON.parse(cutoutUrlsEl?.textContent || '{}');
    } catch (e) {
      this.cutoutUrls = {};
    }

    // A live flash deal lowers the per-ounce price of its candies by the deal percentage (Shopify applies the same discount in the cart),
    // so bag totals here match what the cart will show.
    candies.forEach((c) => {
      if (c.flashPct > 0) {
        c.listPrice = parseFloat(c.pricePerOz);
        c.pricePerOz = Math.round(c.listPrice * (100 - c.flashPct)) / 100;
      }
    });

    this.popularHandles = (this.dataset.popular || '').split(',').map((h) => h.trim()).filter(Boolean);
    this.state = new window.MixBuilderState(candies);
    const hMin = parseInt(this.dataset.halloweenMin, 10);
    if (hMin > 0) this.state.minByMode.halloween = hMin;
    this.state.restore();
    this.categoryFilter = 'all';
    this.sortMode = 'popular';
    this.dietFilters = new Set();
    // The wall starts with about a dozen picks; "Show all" opens the rest. Deep links (?add=, ?focus=) need their card on the wall, so they open it all.
    this.showAll = /[?&](add|focus)=/.test(window.location.search);

    // Resolve real piece image(s) per candy (real product image, else
    // name-matched isolated cutouts — some candies have multiple color
    // variants, e.g. Ahlgrens comes in green/pink/white) so physics pieces
    // can be textured with them instead of a flat color dot.
    Object.values(this.state.candyById).forEach((candy) => {
      const tempKey = window.matchTempCandyPhoto ? window.matchTempCandyPhoto(candy.name) : null;
      const tempVariants = tempKey ? this.tempPhotoUrls[tempKey] : null;
      // a real product photo wins; a placeholder cutout we uploaded ourselves
      // (candy-cut-*) does not, so the multi-face variants still show
      const isPlaceholder = candy.image && /candy-cut-/.test(candy.image);
      candy.resolvedImages = candy.image && !(isPlaceholder && tempVariants) ? [candy.image] : tempVariants || [];
      // Card thumbnail: real product photo wins, else the brighter multi-piece
      // composite if we made one, else fall back to the same image physics uses.
      const hasRealImage = candy.image && !isPlaceholder;
      candy.cardImage = hasRealImage ? candy.image : (tempKey && this.cardPhotoUrls[tempKey]) || candy.resolvedImages[0] || null;
      // Halloween candies have no isolated cutouts: the product photo is a pile
      // of candy, so it stays on the card and the physics pieces use plain
      // colored dots instead of a mushy pile texture.
      if (candy.mode === 'halloween') {
        candy.resolvedImages = [];
        candy.cardImage = candy.image || null;
      }
      // A rendered cut-out for this exact product beats everything above.
      const cutout = this.cutoutUrls[candy.handle];
      if (cutout) {
        candy.resolvedImages = [cutout];
        if (!candy.cardImage) candy.cardImage = cutout;
      }
    });

    this._initPhysics();
    // The physics world is sized once at construction; if the panel later
    // resizes (layout settling, rotation, window resize) the walls no
    // longer match the drawn bag — so rebuild the world at the new size.
    const wrap = this.querySelector('.scale__canvas-wrap');
    if (wrap && 'ResizeObserver' in window) {
      let lastW = wrap.clientWidth, lastH = wrap.clientHeight;
      new ResizeObserver(() => {
        if (Math.abs(wrap.clientWidth - lastW) < 2 && Math.abs(wrap.clientHeight - lastH) < 2) return;
        lastW = wrap.clientWidth; lastH = wrap.clientHeight;
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => this._initPhysics(), 120);
      }).observe(wrap);
    }

    this.game = window.MixGame ? new window.MixGame(this) : null;
    this.state.addEventListener('change', () => this.render());
    this.render();
    this._bindStaticControls();
    this._applyDeepLink();
    this._refreshCartTotal().then(() => { this._autoOpenLastBag(); this.render(); });
    // Bag numbers continue after the bags already in the cart, so keep that count fresh when the cart changes
    // (bags removed in the cart or drawer, cart cleared, or the customer comes back to this tab).
    window.addEventListener('cart:synced', (e) => {
      if (e.detail && e.detail.cart) { this._applyCartBags(e.detail.cart); this.render(); }
    });
    const refreshOnReturn = () => { if (!document.hidden) this._refreshCartTotal().then(() => this.render()); };
    document.addEventListener('visibilitychange', refreshOnReturn);
    window.addEventListener('pageshow', refreshOnReturn);
  }

  // ?add=<product id> (from a product page) drops 1 oz of that candy into the active bag
  _applyDeepLink() {
    // ?mode=halloween (from the Halloween banner/ribbon) opens the Halloween mix, but only while the Halloween button exists
    try {
      const qs = new URLSearchParams(window.location.search);
      const want = qs.get('mode');
      if (want === 'halloween' && this.querySelector('[data-mode-btn="halloween"]')) this._doSwitchMode('halloween');
      // ?mode=classic (from the flash-deal list) makes sure the standard machine is showing, even if this visit was in the Halloween mix
      else if (want === 'classic' && this.state.mode !== 'classic') this._switchMode('classic');
      // ?focus=<product id> scrolls to that candy and highlights it
      const focusId = qs.get('focus');
      if (focusId) {
        setTimeout(() => {
          this.categoryFilter = 'all';
          this.render();
          const card = this.querySelector('[data-card="' + focusId + '"]');
          if (!card) return;
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          card.classList.add('is-focus');
          setTimeout(() => card.classList.remove('is-focus'), 3500);
        }, 4500);
      }
    } catch (e) { /* ignore */ }
    let id = null;
    try { id = new URLSearchParams(window.location.search).get('add'); } catch (e) { return; }
    if (!id) return;
    const candy = this.state.candyById[id];
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('add');
      window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
    } catch (e) { /* ignore */ }
    if (!candy || !candy.inStock) return;
    this.state.addOz(id);
    this._markCard(id);
    setTimeout(() => {
      this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
      this._flash('Added 1 oz of ' + candy.name);
      if (this.game) this.game.check();
    }, 400);
  }

  _initPhysics() {
    const canvas = this.querySelector('[data-scale-canvas]');
    if (!canvas || !window.Matter || !window.MixPhysics) return;
    if (this.physics) this.physics.destroy();
    this.physics = new window.MixPhysics(canvas, this.state.candyById);
    this.physics.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
  }

  _bindStaticControls() {
    this.addEventListener('click', (e) => {
      const modeBtn = e.target.closest('[data-mode-btn]');
      if (modeBtn) {
        this._switchMode(modeBtn.dataset.modeBtn);
        return;
      }

      if (e.target.closest('[data-view-bag]')) {
        const target = this.querySelector('.receipt');
        const sticky = this.querySelector('.scale');
        if (target) {
          const offset = (sticky ? sticky.getBoundingClientRect().height : 0) + 12;
          window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
        }
        return;
      }

      const infoBtn = e.target.closest('[data-info]');
      if (infoBtn) {
        this._openInfo(infoBtn.dataset.info);
        return;
      }

      const addBtn = e.target.closest('[data-add-oz]');
      if (addBtn) {
        this._guardBag(() => this._handleAdd(addBtn));
        return;
      }

      // phones: "+4 oz" adds a quarter pound as four quick ounces (the button is hidden on larger screens)
      const add4Btn = e.target.closest('[data-add-oz4]');
      if (add4Btn) {
        const id4 = add4Btn.dataset.addOz4;
        const stand = { dataset: { addOz: id4 }, closest: (sel) => add4Btn.closest(sel) };
        this._guardBag(() => [0, 1, 2, 3].forEach((n) => setTimeout(() => this._handleAdd(stand), n * 320)));
        return;
      }

      const removeBtn = e.target.closest('[data-remove-oz]');
      if (removeBtn) {
        const candyId = removeBtn.dataset.removeOz;
        this.state.removeOz(candyId);
        this.physics?.remove(candyId, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
        this._haptic(6);
        if (this.game) this.game.sound.remove();
        return;
      }

      if (e.target.closest('[data-show-all]')) {
        this.showAll = true;
        this.render();
        return;
      }
      if (e.target.closest('[data-mix-surprise]')) {
        this._surprise();
        return;
      }

      const categoryBtn = e.target.closest('[data-filter-category]');
      if (categoryBtn) {
        this.categoryFilter = categoryBtn.dataset.filterCategory;
        this.showAll = false;
        // fill in the pill that was picked (and un-fill the others)
        this.querySelectorAll('[data-filter-category]').forEach((b) => {
          const on = b === categoryBtn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        this.render();
        return;
      }

      const sealedOpen = e.target.closest('[data-sealed-open]');
      if (sealedOpen) {
        this._reopenSealed(Number(sealedOpen.dataset.sealedOpen));
        return;
      }

      const sealedExpand = e.target.closest('[data-sealed-expand]');
      if (sealedExpand) {
        const contents = this.querySelector('#sealedc-' + sealedExpand.dataset.sealedExpand);
        const expanded = sealedExpand.getAttribute('aria-expanded') === 'true';
        sealedExpand.setAttribute('aria-expanded', String(!expanded));
        if (contents) contents.hidden = expanded;
        return;
      }

      const bagPick = e.target.closest('[data-bag-pick]');
      if (bagPick) {
        this.state.selectBag(Number(bagPick.dataset.bagPick));
        this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
        return;
      }

      const bagExpand = e.target.closest('[data-bag-expand]');
      if (bagExpand) {
        const contents = this.querySelector('#bagc-' + bagExpand.dataset.bagExpand);
        const expanded = bagExpand.getAttribute('aria-expanded') === 'true';
        bagExpand.setAttribute('aria-expanded', String(!expanded));
        if (contents) contents.hidden = expanded;
        return;
      }

      const sealedDel = e.target.closest('[data-sealed-del]');
      if (sealedDel) {
        this._removeSealedBag(Number(sealedDel.dataset.sealedDel));
        return;
      }

      const clearAll = e.target.closest('[data-bags-clear]');
      if (clearAll) {
        // two taps: the first asks, the second removes every bag (open ones and the ones already in the cart)
        if (clearAll.dataset.confirm !== '1') {
          clearAll.dataset.confirm = '1';
          clearAll.classList.add('is-confirm');
          clearAll.textContent = 'Tap again to remove all';
          clearTimeout(this._clearAllTimer);
          this._clearAllTimer = setTimeout(() => { this._renderBags(); }, 4000);
          return;
        }
        clearTimeout(this._clearAllTimer);
        this._clearAllBags();
        return;
      }

      const bagDel = e.target.closest('[data-bag-del]');
      if (bagDel) {
        const idx = Number(bagDel.dataset.bagDel);
        const gone = this.state.bags[idx];
        const keys = (gone && gone.replacesKeys) || [];
        this.state.deleteBag(idx);
        this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
        // a bag that was reopened from the cart is still in the cart until sealed again: deleting it must take those lines out too
        if (keys.length) this._removeCartLines(keys, 'Bag ' + (gone.number || '') + ' removed from your cart.');
        return;
      }

      const moveBtn = e.target.closest('[data-bag-move-id]');
      if (moveBtn) {
        // Move sends the candy to the next bag (wrapping) — the keyboard/
        // touch-friendly alternative to dragging, works for any bag count.
        const from = Number(moveBtn.dataset.bagMoveFrom);
        if (this.state.bags.length < 2) return;
        const to = (from + 1) % this.state.bags.length;
        this.state.moveItem(moveBtn.dataset.bagMoveId, from, to);
        this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
        return;
      }

      if (e.target.closest('[data-mix-clear]')) {
        this.state.emptyBag();
        this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
        return;
      }

      if (e.target.closest('[data-bags-new]')) {
        this._newBagWithCheck();
        return;
      }

      if (e.target.closest('[data-mix-seal]')) {
        this._attemptSeal();
        return;
      }
    });

    this.addEventListener('change', (e) => {
      const sortSel = e.target.closest('[data-mix-sort]');
      if (sortSel) { this.sortMode = sortSel.value; this._renderGrid(); return; }
      const dietBox = e.target.closest('[data-filter-diet]');
      if (dietBox) {
        const key = dietBox.dataset.filterDiet;
        if (dietBox.checked) this.dietFilters.add(key);
        else this.dietFilters.delete(key);
        this.render();
      }
    });

    // Native drag-and-drop: drag a candy row from the active bag's receipt
    // onto any bag row to move it there. (Touch devices don't fire native
    // drag events — the Move button is the mobile path.)
    this.addEventListener('dragstart', (e) => {
      const row = e.target.closest('[data-mix-row]');
      if (!row) return;
      e.dataTransfer.setData('text/plain', row.dataset.mixRow);
      e.dataTransfer.effectAllowed = 'move';
      this._dragCandyId = row.dataset.mixRow;
      this._dragFromBag = this.state.activeBagIndex;
    });

    this.addEventListener('dragover', (e) => {
      const target = e.target.closest('[data-bag-drop]');
      if (!target || this._dragCandyId == null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      target.classList.add('is-drop-target');
    });

    this.addEventListener('dragleave', (e) => {
      const target = e.target.closest('[data-bag-drop]');
      if (target) target.classList.remove('is-drop-target');
    });

    this.addEventListener('drop', (e) => {
      const target = e.target.closest('[data-bag-drop]');
      if (!target || this._dragCandyId == null) return;
      e.preventDefault();
      target.classList.remove('is-drop-target');
      const toBag = Number(target.dataset.bagDrop);
      this.state.moveItem(this._dragCandyId, this._dragFromBag, toBag);
      this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
      this._dragCandyId = null;
    });

    this.addEventListener('dragend', () => {
      this._dragCandyId = null;
      this.querySelectorAll('.is-drop-target').forEach((el) => el.classList.remove('is-drop-target'));
    });
  }

  // The bag is empty: the button says "Start now" and takes the visitor straight to the candy.
  _bagIsEmpty() {
    return this.state.bags.every((_, i) => this.state.bagWeight(i) === 0);
  }

  _scrollToCandy() {
    // lands on the first candy cards (just under the sticky bag), so the next tap is already a candy
    const target = this.querySelector('[data-candy-grid]');
    if (!target) return;
    const sticky = this.querySelector('.scale');
    // where the panel sticks (below the header) plus its own height, so the taste buttons land just under it
    const stuckTop = sticky ? parseFloat(getComputedStyle(sticky).top) || 0 : 0;
    const offset = stuckTop + (sticky ? sticky.getBoundingClientRect().height : 0) + 16;
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - offset, behavior: this._reducedMotion() ? 'auto' : 'smooth' });
  }

  async _attemptSeal() {
    if (this._sealing) return;
    if (this._bagIsEmpty()) {
      this._scrollToCandy();
      return;
    }
    if (!this.state.canSeal()) {
      const unmet = this.state.unmetRequirement();
      const need = unmet ? unmet.need : this.state.ozNeededForFirstBag();
      const min = unmet ? unmet.min : this.state.minOz(0);
      const kind = unmet && unmet.mode === 'halloween' ? 'first Halloween bag' : 'first bag';
      const which = unmet && this.state.bags.length > 1 ? 'Bag ' + this.state.bagNumber(unmet.index) + ': add ' : 'Add ';
      this._setSealStatus(which + need + ' more oz to seal. Your ' + kind + ' needs at least ' + min + ' oz.');
      this._haptic([30, 40, 30]);
      return;
    }

    const items = [];
    this.state.bags.forEach((bag, bagIndex) => {
      // Numbered globally (existing cart bags + this session's position), not
      // reset to 1 each session — otherwise two separate visits could each
      // seal a "Bag 1" and collide on the packing slip.
      const bagNumber = this.state.bagNumber(bagIndex);
      Object.entries(bag.items).forEach(([candyId, oz]) => {
        const candy = this.state.candyById[candyId];
        if (!candy || !candy.variantId || oz <= 0) return;
        items.push({
          id: candy.variantId,
          quantity: oz,
          properties: {
            _pick_your_mix: '1',
            _bag_id: bag.id,
            _bag_number: String(bagNumber),
            _mix_mode: bag.mode || 'classic',
            // marks bulky candy so the packing slip can say the bag is full
            ...(candy.bulky ? { _bulky: '1' } : {}),
            // optional name for the bag's sticker, typed in the cart; kept when a bag is reopened and sealed again
            ...((bag.name || '').trim() ? { _bag_name: bag.name.trim() } : {}),
            // plain (no underscore) so Shopify's checkout and order emails show which bag each candy line belongs to
            Bag: String(bagNumber),
          },
        });
      });
    });
    if (!items.length) return;

    const sealNumbers = this.state.bags.map((_, i) => this.state.bagNumber(i));
    const bagLabel = sealNumbers.length === 1
      ? 'Bag ' + sealNumbers[0]
      : 'Bags ' + Math.min(...sealNumbers) + '–' + Math.max(...sealNumbers);
    const replaceKeys = this.state.bags.flatMap((b) => b.replacesKeys || []);

    this._sealing = true;
    this._setSealStatus('Adding to cart…');

    try {
      if (replaceKeys.length) {
        // bags reopened for editing: take their old lines out of the cart right before the edited version goes in
        const updates = {};
        replaceKeys.forEach((k) => { updates[k] = 0; });
        await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
      }
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart');
      }

      const keepMode = this.state.mode;
      this.state.bags = [{ id: 'bag_' + Math.random().toString(36).slice(2, 10), mode: keepMode, items: {} }];
      this.state.activeBagIndex = 0;
      this.physics?.resetToBag({}, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
      await this._refreshCartTotal();
      this._refreshCartPill();
      this._setSealStatus(bagLabel + ' added to your cart!');
      this._celebrate('Sealed! ' + bagLabel + ' added to your cart', [40, 60, 40, 60, 90], 'seal');
      if (this.game) this.game.check({ sealed: true });
      this.dispatchEvent(new CustomEvent('mix:sealed', { bubbles: true }));
      this.state._emitChange();
    } catch (e) {
      this._setSealStatus(e.message || 'Something went wrong — try again.');
    } finally {
      this._sealing = false;
    }
  }

  _reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Phones (95% of visits) skip the Halloween light show: lightning, fog and per-candy scenes slow them down.
  _isPhone() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  _haptic(pattern) {
    try { if (navigator.vibrate && (!navigator.userActivation || navigator.userActivation.hasBeenActive)) navigator.vibrate(pattern); } catch (e) { /* not supported */ } // only after a tap: browsers log an error otherwise
  }

  // ---- Bags (Ash, 2026-10-30) -------------------------------------------------------------------------------
  // A full bag starts the next one by itself (and says so). Otherwise a new bag only starts when the customer asks for it,
  // and if another bag still has room we ask first, so nobody ends up with four half-empty bags by accident.
  _bagRoom(i) { return 15 - this.state.bagSpace(i); }
  _bagHasRoom(i) { const b = this.state.bags[i]; return !!b && (b.mode || 'classic') === this.state.mode && this.state.bagWeight(i) > 0 && this._bagRoom(i) >= 1; }

  _bagFullNotice(text) {
    let el = this.querySelector('[data-pym-fullnote]');
    if (!el) { el = document.createElement('div'); el.className = 'pym-fullnote'; el.setAttribute('data-pym-fullnote', ''); el.setAttribute('role', 'status'); this.appendChild(el); }
    el.textContent = text;
    el.classList.add('is-on');
    clearTimeout(this._fullNoteTimer);
    this._fullNoteTimer = setTimeout(() => el.classList.remove('is-on'), 4200);
  }

  _askBag(opts, done) {
    let dlg = this.querySelector('[data-pym-askbag]');
    if (!dlg) {
      dlg = document.createElement('dialog');
      dlg.className = 'pym-askbag';
      dlg.setAttribute('data-pym-askbag', '');
      dlg.innerHTML = '<button type="button" class="pym-askbag__x" data-ask="cancel" aria-label="Close">&times;</button><h3 class="pym-askbag__h" data-ask-h></h3><p class="pym-askbag__p" data-ask-p></p><button type="button" class="pym-askbag__yes" data-ask="yes"></button><button type="button" class="pym-askbag__no" data-ask="no"></button>';
      this.appendChild(dlg);
      dlg.addEventListener('click', (ev) => {
        const b = ev.target.closest('[data-ask]');
        if (b) { const cb = dlg._cb; dlg._cb = null; dlg.close(); if (cb && b.dataset.ask !== 'cancel') cb(b.dataset.ask); }
        else if (ev.target === dlg) { dlg._cb = null; dlg.close(); }
      });
      dlg.addEventListener('cancel', () => { dlg._cb = null; });
    }
    dlg.querySelector('[data-ask-h]').textContent = opts.title;
    dlg.querySelector('[data-ask-p]').textContent = opts.text;
    dlg.querySelector('.pym-askbag__yes').textContent = opts.yes;
    dlg.querySelector('.pym-askbag__no').textContent = opts.no;
    dlg._cb = done;
    if (typeof dlg.showModal === 'function') dlg.showModal(); else done('no');
  }

  // Before adding candy: if the bag you are on is not the first one that still has room, ask once.
  _guardBag(proceed) {
    const st = this.state;
    const cur = st.activeBagIndex;
    let open = -1;
    st.bags.forEach((b, i) => { if (open === -1 && i !== cur && this._bagHasRoom(i)) open = i; });
    if (open === -1 || cur < open && this._bagHasRoom(cur)) { proceed(); return; }
    const key = open + '>' + cur + '>' + st.bags.length;
    if (this._askedBagKey === key) { proceed(); return; }
    const n = st.bagNumber(open), c = st.bagNumber(cur), room = Math.max(1, Math.floor(this._bagRoom(open)));
    this._askBag({
      title: 'Bag ' + n + ' is not full yet',
      text: 'You are on bag ' + c + ', but bag ' + n + ' still has room for about ' + room + ' oz. Add this candy to bag ' + n + '?',
      yes: 'Add to bag ' + n,
      no: 'Stay on bag ' + c,
    }, (choice) => {
      if (choice === 'yes') {
        const target = st.bags[open];
        const empty = st.bagWeight(cur) === 0 && st.bags.length > 1;
        if (empty) st.deleteBag(cur);          // do not leave the empty bag behind
        st.selectBag(st.bags.indexOf(target));
        this.physics?.resetToBag(st.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
      } else this._askedBagKey = key;
      proceed();
    });
  }

  // "+ New bag": if a bag with candy in it still has room, ask before starting another one.
  _newBagWithCheck() {
    const st = this.state;
    const cur = st.activeBagIndex;
    const start = () => { st.startNewBag(); this.physics?.resetToBag(st.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ); };
    if (st.bagWeight(cur) === 0) { this._bagFullNotice('You already have an empty bag to fill.'); return; }
    let open = -1;
    if (this._bagHasRoom(cur)) open = cur; else st.bags.forEach((b, i) => { if (open === -1 && this._bagHasRoom(i)) open = i; });
    if (open === -1) { start(); return; }
    const n = st.bagNumber(open), room = Math.max(1, Math.floor(this._bagRoom(open)));
    this._askBag({
      title: 'Bag ' + n + ' is not full yet',
      text: 'It still has room for about ' + room + ' oz. Keep filling it, or start a new bag?',
      yes: 'Keep filling bag ' + n,
      no: 'Start a new bag',
    }, (choice) => {
      if (choice === 'yes') { if (open !== cur) { st.selectBag(open); this.physics?.resetToBag(st.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ); } }
      else start();
    });
  }

  _handleAdd(addBtn) {
    const C = window.MIX_BUILDER_CONSTANTS;
    const candyId = addBtn.dataset.addOz;
    const candy = this.state.candyById[candyId];
    const card = addBtn.closest('.flav-card');
    const fromEl = (card && card.querySelector('.flav-card__img')) || addBtn;
    const bagBefore = this.state.activeBagIndex;
    // Minimum unlock is only ever about bag #1 — irrelevant once it's
    // already secured, or when the oz is going into any other bag.
    const trackBag1 = this.state.cartBagCount === 0 && bagBefore === 0;
    const bag1OzBefore = trackBag1 ? this.state.bagWeight(0) : null;

    this.state.addOz(candyId);

    const newBag = this.state.activeBagIndex !== bagBefore;
    this._haptic(8);
    if (this.game) this.game.sound.tap();
    this._markCard(candyId);
    if (candy.mode === 'halloween') this._candyFx(candyId, 'card');

    if (newBag) {
      // crossing 15 oz starts a fresh bag with no pieces yet, so resync
      this.physics?.resetToBag(this.state.activeBag.items, C.PIECES_PER_OZ);
      const nextBagNumber = this.state.bagNumber(this.state.activeBagIndex);
      this._celebrate('Bag full! On to bag ' + nextBagNumber, [30, 50, 30, 50, 60], 'full');
      if (this._isPhone()) this._bagFullNotice('Bag ' + this.state.bagNumber(bagBefore) + ' is full. Started bag ' + nextBagNumber + '.');
      if (this.game) this.game.check();
      return;
    }

    // each ounce sends PIECES_PER_OZ pieces flying from the card into the bag
    for (let n = 0; n < C.PIECES_PER_OZ; n++) {
      setTimeout(() => {
        this._flyToBag(fromEl, candy, () => {
          if (this.state.activeBagIndex === bagBefore) this.physics?.drop(candyId, 1);
          if (this.game) this.game.sound.land();
          if (n === C.PIECES_PER_OZ - 1) {
            this._landed('+1 oz');
            if (candy.mode === 'halloween') this._candyFx(candyId, 'bag');
          }
        });
      }, n * 110);
    }

    if (trackBag1 && bag1OzBefore < this.state.minOz() && this.state.bagWeight(0) >= this.state.minOz()) {
      setTimeout(() => this._celebrate('Minimum unlocked! You can seal it', [20, 40, 20, 40, 40], 'minimum'), 620);
    }
    if (this.game) this.game.check();
  }

  // Per-candy effects: the card glows/jolts on tap, and the hand-drawn scene
  // for this candy plays inside the bag when the piece lands.
  _candyFx(candyId, target) {
    if (this._reducedMotion() || this._isPhone() || !document.body.animate || !window.MixFX) return;
    const candy = this.state.candyById[candyId];
    if (!candy) return;
    const meta = window.MixFX.meta(candy.handle);
    const onBag = target === 'bag';
    const host = onBag ? this.querySelector('.scale__canvas-wrap') : this.querySelector('[data-card="' + candyId + '"]');
    if (!host) return;
    this._fxBusy = this._fxBusy || 0;
    if (this._fxBusy >= 3) return;
    this._fxBusy += 1;

    const layer = document.createElement('div');
    layer.className = 'cfx' + (onBag ? ' cfx--bag' : '');
    layer.setAttribute('aria-hidden', 'true');
    host.appendChild(layer);

    if (onBag) {
      window.MixFX.scene(candy.handle, layer);
    } else {
      const img = host.querySelector('.flav-card__img');
      const r = host.getBoundingClientRect(), ir = img ? img.getBoundingClientRect() : r;
      const glow = document.createElement('div');
      glow.className = 'cfx__glow';
      glow.style.background = 'radial-gradient(circle at ' + (ir.left - r.left + ir.width / 2) + 'px ' + (ir.top - r.top + ir.height / 2) + 'px, ' + meta.c + ' 0%, transparent 62%)';
      layer.appendChild(glow);
      glow.animate([{ opacity: 0 }, { opacity: 0.75, offset: 0.2 }, { opacity: 0 }], { duration: 800, easing: 'ease-out' });
      if (meta.k && meta.k !== 'none' && CARD_ANIMS[meta.k]) host.animate(CARD_ANIMS[meta.k], { duration: 520, easing: 'ease-in-out' });
    }
    setTimeout(() => {
      layer.remove();
      this._fxBusy = Math.max(0, this._fxBusy - 1);
    }, onBag ? 2000 : 900);
  }

  // The candy's drawn icon flies from the card into the bag.
  _flyEmoji(fromEl, candy, done) {
    const target = this.querySelector('.scale__canvas-wrap');
    if (!fromEl || !target || this._reducedMotion() || !fromEl.animate || !window.MixFX) { done(); return; }
    const meta = window.MixFX.meta(candy.handle);
    const a = fromEl.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const size = 44;
    const el = window.MixFX.icon(candy.handle);
    Object.assign(el.style, { position: 'fixed', left: '0', top: '0', zIndex: '60', pointerEvents: 'none', filter: 'drop-shadow(0 0 8px ' + meta.c + ')' });
    document.body.appendChild(el);
    const sx = a.left + a.width / 2 - size / 2, sy = a.top + a.height / 2 - size / 2;
    const ex = b.left + b.width / 2 - size / 2, ey = b.top + b.height * 0.3 - size / 2;
    const mx = (sx + ex) / 2 + (Math.random() - 0.5) * 80, my = Math.min(sy, ey) - 70;
    const anim = el.animate([
      { transform: 'translate(' + sx + 'px,' + sy + 'px) scale(0.8) rotate(0deg)', opacity: 1 },
      { transform: 'translate(' + mx + 'px,' + my + 'px) scale(1.4) rotate(160deg)', opacity: 1, offset: 0.5 },
      { transform: 'translate(' + ex + 'px,' + ey + 'px) scale(0.8) rotate(340deg)', opacity: 0.95 },
    ], { duration: 560, easing: 'cubic-bezier(0.3, 0.1, 0.5, 1)' });
    let finished = false;
    const finish = () => { if (finished) return; finished = true; el.remove(); done(); };
    anim.onfinish = finish;
    setTimeout(finish, 850);
  }

  _markCard(candyId) {
    const card = this.querySelector('[data-card="' + candyId + '"]');
    if (!card) return;
    card.classList.remove('is-hit');
    void card.offsetWidth;
    card.classList.add('is-hit');
  }

  _flyToBag(fromEl, candy, done) {
    const target = this.querySelector('.scale__canvas-wrap');
    if (candy && candy.mode === 'halloween') { this._flyEmoji(fromEl, candy, done); return; }
    const src = candy && candy.resolvedImages && candy.resolvedImages[0];
    if (!fromEl || !target || !src || this._reducedMotion() || !fromEl.animate) { done(); return; }
    const a = fromEl.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const size = 44;
    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.className = 'pym-fly';
    Object.assign(img.style, { position: 'fixed', left: '0', top: '0', width: size + 'px', height: size + 'px', objectFit: 'contain', zIndex: '60', pointerEvents: 'none' });
    document.body.appendChild(img);
    const sx = a.left + a.width / 2 - size / 2, sy = a.top + a.height / 2 - size / 2;
    const ex = b.left + b.width / 2 - size / 2, ey = b.top + b.height * 0.12 - size / 2;
    const mx = (sx + ex) / 2 + (Math.random() - 0.5) * 40, my = Math.min(sy, ey) - 50;
    const anim = img.animate([
      { transform: 'translate(' + sx + 'px,' + sy + 'px) scale(1) rotate(0deg)', opacity: 1 },
      { transform: 'translate(' + mx + 'px,' + my + 'px) scale(1.25) rotate(140deg)', opacity: 1, offset: 0.5 },
      { transform: 'translate(' + ex + 'px,' + ey + 'px) scale(0.7) rotate(300deg)', opacity: 0.9 },
    ], { duration: 520, easing: 'cubic-bezier(0.3, 0.1, 0.5, 1)' });
    let finished = false;
    const finish = () => { if (finished) return; finished = true; img.remove(); done(); };
    anim.onfinish = finish;
    setTimeout(finish, 800);
  }

  _landed(label) {
    const wrap = this.querySelector('.scale__canvas-wrap');
    const fx = this.querySelector('[data-scale-fx]');
    if (wrap && !this._reducedMotion()) {
      wrap.classList.remove('is-bump');
      void wrap.offsetWidth;
      wrap.classList.add('is-bump');
    }
    if (fx && !this._reducedMotion()) {
      const pop = document.createElement('span');
      pop.className = 'scale__pop';
      pop.textContent = label;
      fx.appendChild(pop);
      setTimeout(() => pop.remove(), 900);
    }
    const num = this.querySelector('[data-scale-oz]');
    if (num) {
      num.classList.remove('is-pop');
      void num.offsetWidth;
      num.classList.add('is-pop');
    }
  }

  _flash(text, kind) {
    // Phones: no pop-up banners ("Added 1 oz of...", "Surprise! We added..."). The panel and the bag list already show the result, and the banner covered them.
    if (this._isPhone()) return;
    this._banners = this._banners || [];
    this._banners.push({ text, kind });
    if (!this._bannerBusy) this._nextBanner();
  }

  _nextBanner() {
    const el = this.querySelector('[data-scale-flash]');
    const item = (this._banners || []).shift();
    if (!el || !item) { this._bannerBusy = false; if (el) el.classList.remove('is-on'); return; }
    this._bannerBusy = true;
    el.textContent = item.text;
    el.classList.add('is-on');
    if (item.kind && this.game) this.game.sound[item.kind]?.();
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => {
      el.classList.remove('is-on');
      setTimeout(() => this._nextBanner(), 220);
    }, 1700);
  }

  _celebrate(text, vibration, kind, confetti = true) {
    this._flash(phoneWords(text), kind);
    // Only sealing a bag gets the buzz and confetti; everything else is a quiet one-line message.
    if (kind === 'seal') {
      this._haptic(vibration);
      if (confetti) this._confetti();
    }
  }

  _confetti() {
    if (this._reducedMotion()) return;
    const wrap = this.querySelector('.scale__canvas-wrap');
    if (!wrap || !wrap.animate) return;
    const r = wrap.getBoundingClientRect();
    const ox = r.left + r.width / 2, oy = r.top + r.height * 0.3;
    const colors = ['#F7B52C', '#D0155C', '#9ED36A', '#F6C3D5', '#FFFCFA', '#5BC0EB'];
    for (let i = 0; i < 30; i++) {
      const p = document.createElement('span');
      p.className = 'pym-conf';
      p.style.background = colors[i % colors.length];
      document.body.appendChild(p);
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2.4;
      const speed = 90 + Math.random() * 170;
      const dx = Math.cos(ang) * speed, dy = Math.sin(ang) * speed;
      const fall = 140 + Math.random() * 120;
      const rot = (Math.random() - 0.5) * 900;
      const a = p.animate([
        { transform: 'translate(' + ox + 'px,' + oy + 'px) rotate(0deg)', opacity: 1 },
        { transform: 'translate(' + (ox + dx) + 'px,' + (oy + dy) + 'px) rotate(' + rot / 2 + 'deg)', opacity: 1, offset: 0.4 },
        { transform: 'translate(' + (ox + dx * 1.3) + 'px,' + (oy + dy + fall) + 'px) rotate(' + rot + 'deg)', opacity: 0 },
      ], { duration: 1000 + Math.random() * 400, easing: 'cubic-bezier(0.2, 0.6, 0.4, 1)' });
      a.onfinish = () => p.remove();
      setTimeout(() => p.remove(), 1600);
    }
  }

  _setSealStatus(message) {
    message = phoneWords(message);
    this._sealMessage = message;
    const el = this.querySelector('[data-scale-status]');
    if (el) el.textContent = message;
    if (message) {
      clearTimeout(this._sealMessageTimer);
      this._sealMessageTimer = setTimeout(() => {
        this._sealMessage = null;
        this.render();
      }, 2500);
    }
  }

  _applyCartBags(cart) {
    const mixItems = cart.items.filter((item) => item.properties && item.properties._pick_your_mix);
    this.state.cartOzTotal = mixItems.reduce((sum, item) => sum + item.quantity, 0);
    const byNumber = {};
    mixItems.forEach((item) => {
      const n = Number(item.properties._bag_number);
      if (!n) return;
      const bag = byNumber[n] || (byNumber[n] = { number: n, id: item.properties._bag_id || ('cart_' + n), mode: item.properties._mix_mode || 'classic', items: {}, lineKeys: [], oz: 0, price: 0, canReopen: true, name: item.properties._bag_name || '' });
      const candy = this.state.candies.find((c) => String(c.variantId) === String(item.variant_id));
      if (candy) bag.items[candy.id] = (bag.items[candy.id] || 0) + item.quantity; else bag.canReopen = false;
      bag.lineKeys.push(item.key);
      bag.oz += item.quantity;
      bag.price += item.final_line_price / 100;
    });
    const bags = Object.values(byNumber).sort((x, y) => x.number - y.number);
    this.state.cartBags = bags;
    this.state.cartBagCount = bags.length;
    this.state.cartBagMax = bags.length ? bags[bags.length - 1].number : 0;
  }

  // Take whole machine bags out of the real cart (the same lines the cart page's "Remove bag" button clears)
  async _removeCartLines(keys, message) {
    if (!keys || !keys.length) return;
    const updates = {};
    keys.forEach((k) => { updates[k] = 0; });
    try {
      const res = await fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
      if (!res.ok) throw new Error('Could not update the cart');
      await this._refreshCartTotal();
      this._refreshCartPill();
      if (typeof window.refreshMilestones === 'function') window.refreshMilestones();
      if (message) this._setSealStatus(message);
    } catch (err) {
      this._setSealStatus('Could not remove that bag, try again.');
    }
    this.state._emitChange();
    this.render();
  }

  async _removeSealedBag(number) {
    const sealed = (this.state.cartBags || []).find((b) => b.number === number);
    if (!sealed) return;
    await this._removeCartLines(sealed.lineKeys, 'Bag ' + number + ' removed from your cart.');
  }

  async _clearAllBags() {
    const keys = (this.state.cartBags || []).flatMap((b) => b.lineKeys || []);
    this.state.resetAllBags();
    this.physics?.resetToBag({}, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
    if (keys.length) await this._removeCartLines(keys, 'All bags removed.');
    else this._setSealStatus('All bags cleared.');
  }

  // Coming back to the machine (for example "Continue shopping" from the cart): if the last bag in the cart still has room,
  // go back to that bag instead of starting a new one. A new bag only starts when the one before it is full (Ash, 2026-10-30).
  _autoOpenLastBag() {
    if (this._autoOpened) return;
    this._autoOpened = true;
    const st = this.state;
    if (st.bags.some((b, i) => st.bagWeight(i) > 0)) return;           // already working on candy this visit
    const mine = (st.cartBags || []).filter((b) => b.canReopen && (b.mode || 'classic') === st.mode);
    if (!mine.length) return;
    const last = mine[mine.length - 1];
    const space = Object.entries(last.items).reduce((sum, [id, oz]) => sum + oz * st.spaceOf(id), 0);
    if (15 - space < 1) return;                                        // that bag is full, so a new bag is right
    this._reopenSealed(last.number);
  }

  async _reopenSealed(number) {
    const sealed = this.state.cartBags.find((b) => b.number === number);
    if (!sealed || !sealed.canReopen) return;
    // already being edited? just jump to it
    const open = this.state.bags.findIndex((b) => b.number === number);
    if (open >= 0) { this.state.selectBag(open); this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ); return; }
    this.state.loadSealedBag(sealed);
    this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
    this._setSealStatus('Bag ' + number + ' is open. Add or remove candy, then seal it again.');
  }

  // keep the header's "Cart 0" pill in step with what was just added (it is rendered by the cart-icon-bubble section)
  async _refreshCartPill() {
    try {
      const res = await fetch('/?sections=cart-icon-bubble');
      const data = await res.json();
      const html = new DOMParser().parseFromString(data['cart-icon-bubble'] || '', 'text/html');
      const src = html.querySelector('.shopify-section');
      const dst = document.getElementById('cart-icon-bubble');
      if (src && dst) dst.innerHTML = src.innerHTML;
    } catch (e) {
      // header count just catches up on the next page load
    }
  }

  async _refreshCartTotal() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this._applyCartBags(cart);
    } catch (e) {
      // network hiccup — values just stay at their last known value
    }
  }

  _switchMode(mode) {
    if (this._switching || mode === this.state.mode) return;
    // Switching away from a bag that already has candy would start a separate
    // bag of the other type, so ask first. No question if there's nothing in
    // the bag, or if a bag of the target type already exists to switch to.
    const hasCandy = this.state.bagWeight(this.state.activeBagIndex) > 0;
    const targetExists = this.state.bags.some((b) => (b.mode || 'classic') === mode);
    if (hasCandy && !targetExists) {
      this._confirmModeSwitch(mode).then((ok) => { if (ok) this._doSwitchMode(mode); });
      return;
    }
    this._doSwitchMode(mode);
  }

  _confirmModeSwitch(mode) {
    return new Promise((resolve) => {
      const from = this.state.mode === 'halloween' ? 'Halloween' : 'Classic';
      const to = mode === 'halloween' ? 'Halloween' : 'Classic';
      const oz = this.state.bagWeight(this.state.activeBagIndex);
      const dialog = document.createElement('dialog');
      dialog.className = 'pym-info';
      dialog.setAttribute('aria-labelledby', 'pym-switch-title');
      dialog.innerHTML =
        '<form method="dialog" class="pym-info__inner">' +
        '<h3 class="pym-info__title" id="pym-switch-title">Start a ' + to + ' bag?</h3>' +
        '<p class="pym-info__text">Your ' + from + ' bag (' + oz + ' oz) stays saved. ' + to +
        ' candy goes in its own separate bag, because the two types are packed apart.</p>' +
        '<button class="pym-info__close" value="yes">Yes, start a ' + to + ' bag</button>' +
        '<button class="pym-info__close pym-info__close--ghost" value="no">Stay on ' + from + '</button>' +
        '</form>';
      dialog.addEventListener('close', () => {
        resolve(dialog.returnValue === 'yes');
        dialog.remove();
      });
      this.appendChild(dialog);
      dialog.showModal();
    });
  }

  _doSwitchMode(mode) {
    if (this._switching) return;
    const apply = () => {
      // the Halloween mix has no vegan / gluten-free filters, so clear them so no hidden filter keeps candy out of the list
      if (mode === 'halloween') {
        this.dietFilters.clear();
        this.querySelectorAll('[data-filter-diet]').forEach((b) => { b.checked = false; });
      }
      this.categoryFilter = 'all';
      this.querySelectorAll('[data-filter-category]').forEach((b) => {
        b.classList.toggle('is-active', b.dataset.filterCategory === 'all');
      });
      this.state.setMode(mode);
      this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
    };
    this._haptic(10);
    if (this.game) this.game.sound.tap();
    if (mode === 'halloween' && !this._reducedMotion() && !this._isPhone() && document.body.animate) {
      // the page flips to Halloween while the screen is blacked out
      this._switching = true;
      this._spookIn(apply, () => { this._switching = false; });
      return;
    }
    apply();
    if (mode === 'classic') this._candyPop();
  }

  _fxOrigin() {
    const btn = this.querySelector('[data-mode-btn="halloween"]');
    const r = btn ? btn.getBoundingClientRect() : { left: innerWidth / 2, top: 120, width: 0, height: 0 };
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  _fxLayer(cls) {
    const layer = document.createElement('div');
    layer.className = 'pym-fx ' + cls;
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    return layer;
  }

  // Halloween entrance, ~3.4 s: lights flicker out, lightning, red eyes in the
  // dark, the page flips while blacked out, a second strike, then a vortex of
  // bats bursts out and embers start to rise.
  _spookIn(onCovered, onDone) {
    const { x: ox, y: oy } = this._fxOrigin();
    const layer = this._fxLayer('pym-fx--spook');
    const W = innerWidth, H = innerHeight;
    const NS = 'http://www.w3.org/2000/svg';

    const dark = document.createElement('div');
    dark.className = 'pym-fx__dark';
    layer.appendChild(dark);
    // 0 - 0.75 s: failing-bulb flicker down to full black; hold; lift at 1.9 s
    dark.animate(
      [
        { opacity: 0 }, { opacity: 0.5, offset: 0.06 }, { opacity: 0.08, offset: 0.11 }, { opacity: 0.7, offset: 0.17 },
        { opacity: 0.2, offset: 0.2 }, { opacity: 0.9, offset: 0.26 }, { opacity: 1, offset: 0.3 }, { opacity: 1, offset: 0.55 },
        { opacity: 0, offset: 0.7 }, { opacity: 0 },
      ],
      { duration: 2700, fill: 'both' }
    );

    const flash = document.createElement('div');
    flash.className = 'pym-fx__flash';
    layer.appendChild(flash);
    const strike = (delay) => {
      flash.animate([{ opacity: 0 }, { opacity: 0.55 }, { opacity: 0.08 }, { opacity: 0.35 }, { opacity: 0 }], { duration: 520, delay, fill: 'none' });
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('class', 'pym-fx__bolt');
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
      const mk = (x0, y0, x1, y1, w) => {
        let d = 'M' + x0 + ' ' + y0, x = x0, y = y0;
        const steps = 9;
        for (let i = 1; i <= steps; i++) {
          x = x0 + ((x1 - x0) * i) / steps + (Math.random() - 0.5) * 70;
          y = y0 + ((y1 - y0) * i) / steps;
          d += ' L' + x.toFixed(0) + ' ' + y.toFixed(0);
        }
        const path = document.createElementNS(NS, 'path');
        path.setAttribute('d', d);
        path.setAttribute('stroke-width', w);
        svg.appendChild(path);
        return [x, y];
      };
      const sx = W * (0.15 + Math.random() * 0.7);
      const [ex, ey] = mk(sx, -10, sx + (Math.random() - 0.5) * 200, H * 0.62, 4);
      mk(sx + (ex - sx) * 0.35, H * 0.2, sx + (Math.random() - 0.3) * 320, H * 0.5, 2);
      layer.appendChild(svg);
      svg.animate([{ opacity: 0 }, { opacity: 1, offset: 0.06 }, { opacity: 0.2, offset: 0.2 }, { opacity: 0.9, offset: 0.3 }, { opacity: 0 }], { duration: 520, delay, fill: 'both' });
      setTimeout(() => svg.remove(), delay + 600);
    };
    strike(520);

    // glowing red eyes blinking open in the blackout
    for (let i = 0; i < 9; i++) {
      const eyes = document.createElement('span');
      eyes.className = 'pym-fx__eyes';
      eyes.style.left = 6 + Math.random() * 88 + '%';
      eyes.style.top = 12 + Math.random() * 76 + '%';
      eyes.style.setProperty('--gap', 10 + Math.random() * 14 + 'px');
      layer.appendChild(eyes);
      eyes.animate(
        [{ opacity: 0, transform: 'scaleY(0.1)' }, { opacity: 1, transform: 'scaleY(1)', offset: 0.2 }, { opacity: 1, transform: 'scaleY(1)', offset: 0.55 }, { opacity: 1, transform: 'scaleY(0.08)', offset: 0.62 }, { opacity: 1, transform: 'scaleY(1)', offset: 0.7 }, { opacity: 0, transform: 'scaleY(1)' }],
        { duration: 1200 + Math.random() * 500, delay: 650 + Math.random() * 600, fill: 'both' }
      );
    }

    // flip the page while it's black (~0.9 s), then second strike as it lifts
    setTimeout(() => { if (onCovered) onCovered(); }, 950);
    strike(1500);

    // bat vortex: spirals out of the toggle's position, then off the screen
    const batSvg =
      '<svg viewBox="0 0 64 40" width="64" height="40" focusable="false"><g class="pym-bat__wings">' +
      '<path d="M32 22 C24 8 12 4 0 12 C8 14 8 22 12 30 C17 24 22 27 26 33 C28 29 30 27 32 27Z" fill="#0c0314"/>' +
      '<path d="M32 22 C40 8 52 4 64 12 C56 14 56 22 52 30 C47 24 42 27 38 33 C36 29 34 27 32 27Z" fill="#0c0314"/></g>' +
      '<ellipse cx="32" cy="25" rx="6" ry="9" fill="#150620"/><path d="M27 18 L25 9 L30 15Z M37 18 L39 9 L34 15Z" fill="#150620"/>' +
      '<circle cx="29.6" cy="22" r="1.7" fill="#ff2a2a"/><circle cx="34.4" cy="22" r="1.7" fill="#ff2a2a"/></svg>';
    const cx = W / 2, cy = H * 0.42;
    for (let i = 0; i < 46; i++) {
      const el = document.createElement('span');
      el.className = 'pym-bat';
      el.innerHTML = batSvg;
      layer.appendChild(el);
      const th0 = (i / 46) * Math.PI * 2, dir = i % 2 ? 1 : -1;
      const sc = 0.5 + Math.random() * 0.9, big = Math.hypot(W, H);
      const frames = [];
      const N = 9;
      for (let k = 0; k <= N; k++) {
        const u = k / N;
        const r = 20 + Math.pow(u, 1.6) * big * (0.55 + Math.random() * 0.05);
        const th = th0 + dir * u * 5.2;
        frames.push({
          transform: 'translate(' + (cx + Math.cos(th) * r - 32).toFixed(0) + 'px,' + (cy + Math.sin(th) * r * 0.7 - 20).toFixed(0) + 'px) rotate(' + (Math.sin(th * 2) * 24).toFixed(0) + 'deg) scale(' + (sc * (0.4 + u * 1.3)).toFixed(2) + ')',
          opacity: k === 0 ? 0 : k === N ? 0 : 1,
          offset: u,
        });
      }
      el.animate(frames, { duration: 1700 + Math.random() * 700, delay: 1450 + Math.random() * 350, easing: 'ease-in', fill: 'both' });
    }

    setTimeout(() => { layer.remove(); if (onDone) onDone(); }, 4300);
  }

  // Back to Classic: a candy-pink wipe and a pop of confetti candy.
  _candyPop() {
    if (this._reducedMotion() || !document.body.animate) return;
    const btn = this.querySelector('[data-mode-btn="classic"]');
    const r = btn ? btn.getBoundingClientRect() : { left: innerWidth / 2, top: 120, width: 0, height: 0 };
    const ox = r.left + r.width / 2, oy = r.top + r.height / 2;
    const W = innerWidth, H = innerHeight;
    const layer = this._fxLayer('pym-fx--candy');
    const veil = document.createElement('div');
    veil.className = 'pym-fx__veil pym-fx__veil--candy';
    layer.appendChild(veil);
    veil.animate(
      [{ clipPath: `circle(0px at ${ox}px ${oy}px)`, opacity: 1 }, { clipPath: `circle(${Math.hypot(W, H)}px at ${ox}px ${oy}px)`, opacity: 1, offset: 0.4 }, { clipPath: `circle(${Math.hypot(W, H)}px at ${ox}px ${oy}px)`, opacity: 0 }],
      { duration: 1000, easing: 'cubic-bezier(.3,.1,.2,1)', fill: 'both' }
    );
    const colors = ['#D0155C', '#F7B52C', '#9ED36A', '#4C7FD6', '#F6C3D5', '#FFFFFF'];
    for (let i = 0; i < 36; i++) {
      const c = document.createElement('i');
      c.className = 'pym-confetti';
      c.style.background = colors[i % colors.length];
      c.style.borderRadius = i % 3 ? '3px' : '50%';
      layer.appendChild(c);
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 2.4, v = 260 + Math.random() * 380;
      const dx = Math.cos(a) * v, dy = Math.sin(a) * v;
      c.animate(
        [
          { transform: `translate(${ox}px, ${oy}px) rotate(0deg) scale(0.4)`, opacity: 1 },
          { transform: `translate(${ox + dx}px, ${oy + dy}px) rotate(${Math.random() * 360}deg) scale(1)`, opacity: 1, offset: 0.45 },
          { transform: `translate(${ox + dx * 1.25}px, ${oy + dy + 520}px) rotate(${Math.random() * 720}deg) scale(0.9)`, opacity: 0 },
        ],
        { duration: 1300 + Math.random() * 600, delay: Math.random() * 120, easing: 'cubic-bezier(.2,.7,.4,1)', fill: 'both' }
      );
    }
    setTimeout(() => layer.remove(), 2600);
  }

  _renderMode() {
    const mode = this.state.mode;
    this.dataset.mode = mode;
    this.querySelectorAll('[data-mode-btn]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.modeBtn === mode));
    });
    // The minimum only ever applies to the first bag of the order, and it is the
    // first bag's mode that sets it. Once that bag is in the cart (or this
    // session's first bag has reached it), no other bag has a minimum, whatever
    // mode is showing.
    const fbi = Math.max(0, this.state.firstBagOfMode(mode));
    const coveredBefore = this.state.modeCovered(mode);
    const reachedNow = !coveredBefore && this.state.bagWeight(fbi) >= this.state.minOz(fbi);
    this.querySelectorAll('[data-mode-text]').forEach((el) => {
      let key = mode;
      if (el.hasAttribute('data-min-note')) key = coveredBefore ? 'covered' : mode;
      let txt = el.dataset[key];
      // the bag itself just reached the minimum: say so, instead of claiming there is no minimum
      if (el.hasAttribute('data-min-note') && reachedNow) txt = this.state.minOz(fbi) + ' oz minimum reached';
      if (txt != null) el.textContent = txt;
    });
  }

  // Sort the wall (Ash, 2026-10-30). "popular" keeps the original order; the others put in-stock candies first.
  _sortCandies(list) {
    const m = this.sortMode || 'popular';
    if (m === 'popular') return list;
    const byName = (a, b) => String(a.name).localeCompare(String(b.name));
    const stock = (a, b) => (b.inStock ? 1 : 0) - (a.inStock ? 1 : 0);
    const arr = list.slice();
    if (m === 'az') arr.sort((a, b) => stock(a, b) || byName(a, b));
    else if (m === 'za') arr.sort((a, b) => stock(a, b) || byName(b, a));
    else if (m === 'rated') arr.sort((a, b) => stock(a, b) || (Number(b.rating) || 0) - (Number(a.rating) || 0) || byName(a, b));
    return arr;
  }

  _filteredCandies() {
    return this.state.candies.filter((c) => {
      if ((c.mode || 'classic') !== this.state.mode) return false;
      if (this.categoryFilter !== 'all' && !(c.categories || [c.category]).includes(this.categoryFilter)) return false;
      for (const diet of this.dietFilters) {
        if (!c.dietTags.includes(diet)) return false;
      }
      return true;
    });
  }

  render() {
    this._renderMode();
    this._renderScale();
    this._renderBags();
    this._renderReceipt();
    this._renderGrid();
    if (this.game) this.game.render();
    this._announcePending();
  }

  // Tell the sitewide milestone loader how much is sitting in bags that are
  // not in the cart yet, so free shipping / gift progress reflects the mix.
  _announcePending() {
    let cents = 0;
    this.state.bags.forEach((_, i) => { cents += Math.round(this.state.bagPrice(i) * 100); });
    if (cents === this._lastPending) return;
    this._lastPending = cents;
    window.__mixPending = cents;
    window.dispatchEvent(new CustomEvent('mix:pending', { detail: { cents } }));
  }

  _renderScale() {
    const el = this.querySelector('[data-scale-panel]');
    if (!el) return;

    const bagIndex = this.state.activeBagIndex;
    const oz = this.state.bagWeight(bagIndex);
    // How full the bag really is: bulky candy takes almost double room, so a bag with
    // 8 oz of fluffy banana is exactly full even though it only weighs 8 oz.
    const space = this.state.bagSpace(bagIndex);
    const isFull = space >= window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ;
    const price = this.state.bagPrice(bagIndex);
    // The 8 oz minimum only ever applies to bag #1 of the whole order.
    const ozToGo = this.state.ozNeededForBag(bagIndex);
    const minOz = this.state.minOz(bagIndex);
    const bagMode = (this.state.bags[bagIndex] || {}).mode || 'classic';
    const unmetElsewhere = ozToGo === 0 ? this.state.unmetRequirement() : null;

    let status;
    if (this._sealMessage) {
      status = this._sealMessage;
    } else if (isFull) {
      status = oz < window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ ? 'Bag full (fluffy candy takes extra room)' : '15 oz bag full';
    } else if (ozToGo > 0) {
      status = this._bagIsEmpty()
        ? 'Tap a candy to start. ' + minOz + ' oz minimum.'
        : 'Add ' + ozToGo + ' more oz to reach ' + minOz + ' oz.';
    } else if (this._bagIsEmpty()) {
      status = 'Tap a candy to start a new bag.';
    } else if (unmetElsewhere) {
      status = 'Bag ' + this.state.bagNumber(unmetElsewhere.index) + ' needs ' + unmetElsewhere.need + ' more oz before you can seal';
    } else {
      status = 'Ready to seal';
    }

    // The minimum is in real oz but the bar fills by space, so where the minimum
    // sits on the bar depends on what is in the bag: it moves right as bulky candy
    // is added (a bag of only marshmallows reaches 8 oz exactly when it is full).
    const maxSpace = window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ;
    const minSpace = oz > 0 ? Math.min(maxSpace, minOz * (space / oz)) : minOz;

    const fill = el.querySelector('[data-scale-fill]');
    if (fill) {
      fill.style.width = Math.min(100, (space / window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ) * 100) + '%';
      fill.classList.toggle('is-full', isFull);
    }
    const tick = el.querySelector('[data-scale-tick]');
    if (tick) {
      // The "8 min" tick only means anything on bag #1 — hide it once bag #1
      // is already secured, or while viewing any bag other than #1.
      tick.hidden = this.state.requirementFor(bagIndex) === 0;
      tick.classList.toggle('is-hit', ozToGo === 0);
      tick.style.left = (minSpace / window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ) * 100 + '%';
      const em = tick.querySelector('em');
      if (em) em.textContent = minOz + ' min';
    }
    this._renderDialMin(el, minOz, minSpace);

    el.querySelector('[data-scale-oz]').textContent = oz;
    el.querySelector('[data-scale-price]').textContent = '$' + price.toFixed(2);
    el.querySelector('[data-scale-status]').textContent = phoneWords(status);

    const needle = el.querySelector('[data-scale-needle]');
    if (needle) {
      const angle = (Math.min(space, window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ) / 16) * 360; // the dial face is marked 0-16 oz
      needle.style.setProperty('--angle', angle + 'deg');
    }
    // Matches the dial's fixed "8 oz" mark — green once bag #1's own
    // requirement is met, regardless of which bag is currently active.
    el.classList.toggle('is-ready', ozToGo === 0);

    const sealBtn = el.querySelector('[data-mix-seal]');
    if (sealBtn) {
      // stays tappable so a customer who is short of the minimum gets told why, instead of nothing happening
      sealBtn.disabled = this._sealing;
      sealBtn.classList.toggle('is-locked', !this.state.canSeal());
      sealBtn.setAttribute('aria-disabled', String(!this.state.canSeal()));
      // The button always says what happens next: how many oz are missing, or what goes into the cart and for how much.
      if (!this._sealing) {
        let cartTotal = 0;
        this.state.bags.forEach((_, i) => { cartTotal += this.state.bagPrice(i); });
        const empty = this._bagIsEmpty();
        sealBtn.classList.toggle('is-start', empty);
        const short = empty ? 'Start now' : !this.state.canSeal() && ozToGo > 0 ? 'Add ' + ozToGo + ' more oz' : '';
        const lblD = sealBtn.querySelector('.lbl-d'), lblP = sealBtn.querySelector('.lbl-p');
        // the desktop machine keeps "Seal it", phones say "Add to cart"
        if (lblD) lblD.textContent = short || 'Seal it · $' + cartTotal.toFixed(2);
        if (lblP) lblP.textContent = short || 'Add to cart · $' + cartTotal.toFixed(2);
      }
    }
  }

  // Moves the dial's minimum arc + marker to the current mode's minimum.
  _renderDialMin(el, minOz, minSpace = minOz) {
    const key = minOz + '|' + minSpace;
    if (this._dialMin === key) return;
    const dial = el.querySelector('[data-scale-dial]');
    if (!dial) return;
    this._dialMin = key;
    const max = window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ;
    const deg = (minSpace / max) * 360;
    const rad = (deg * Math.PI) / 180;
    const x = (100 + 78 * Math.sin(rad)).toFixed(2);
    const y = (100 - 78 * Math.cos(rad)).toFixed(2);
    const arc = dial.querySelector('.dial__min');
    if (arc) arc.setAttribute('d', 'M' + x + ' ' + y + ' A78 78 0 ' + (360 - deg > 180 ? 1 : 0) + ' 1 100 22');
    const mark = dial.querySelector('.dial__mark');
    if (mark) mark.setAttribute('transform', 'rotate(' + (deg - 180) + ' 100 100)');
    dial.querySelectorAll('.dial__num').forEach((n) => {
      n.classList.toggle('dial__num--min', Math.abs(minSpace - minOz) < 0.01 && n.textContent.trim() === String(minOz));
    });
  }

  _renderBags() {
    const list = this.querySelector('[data-bags-list]');
    const label = this.querySelector('[data-bags-label]');
    if (!list) return;

    // Global bag numbers so they match what will land in the cart and on the packing slip.
    const openNumbers = new Set(this.state.bags.map((b) => b.number).filter(Boolean));
    const sealedRows = (this.state.cartBags || []).filter((b) => !openNumbers.has(b.number));
    const total = sealedRows.length + this.state.bags.length;
    if (label) label.textContent = 'Bag ' + this.state.bagNumber(this.state.activeBagIndex) + ' of ' + (total + (openNumbers.size ? 0 : 0));

    const sealedHtml = sealedRows.map((b) => {
      const items = Object.entries(b.items).map(([candyId, itemOz]) => {
        const candy = this.state.candyById[candyId];
        return `<li>${candy ? candy.name : candyId} <b>${itemOz} oz</b></li>`;
      }).join('');
      return `<li class="bagrow is-sealed" data-sealed-row="${b.number}">
        <span class="bagrow__pick bagrow__pick--sealed"><b>Bag ${b.number}</b>
          <span class="bagrow__w">${b.oz} oz &middot; $${b.price.toFixed(2)}</span>
          <span class="bagrow__st">in cart</span></span>
        ${b.canReopen ? `<button type="button" class="bagrow__edit" data-sealed-open="${b.number}">Open</button>` : ''}
        <button type="button" class="bagrow__open" data-sealed-expand="${b.number}" aria-expanded="false" aria-controls="sealedc-${b.number}" aria-label="Show what is in bag ${b.number}">&#9662;</button>
        <button type="button" class="bagrow__del" data-sealed-del="${b.number}" aria-label="Remove bag ${b.number} from your cart">&times;</button>
        <ul class="bagrow__contents" id="sealedc-${b.number}" hidden>${items}</ul>
      </li>`;
    }).join('');

    list.innerHTML = sealedHtml + this.state.bags
      .map((bag, i) => {
        const oz = this.state.bagWeight(i);
        const price = this.state.bagPrice(i);
        const status = this.state.bagSpace(i) >= window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ ? 'full' : oz > 0 ? oz + ' oz so far' : 'empty';
        const items = Object.entries(bag.items)
          .map(([candyId, itemOz]) => {
            const candy = this.state.candyById[candyId];
            return `<li>${candy ? candy.name : candyId} <b>${itemOz} oz</b>
              <button type="button" class="bagrow__mv" data-bag-move-from="${i}" data-bag-move-id="${candyId}">Move</button></li>`;
          })
          .join('');

        return `<li class="bagrow${i === this.state.activeBagIndex ? ' is-active' : ''}" data-bag-drop="${i}">
          <button type="button" class="bagrow__pick" data-bag-pick="${i}" aria-pressed="${i === this.state.activeBagIndex}">
            <b>Bag ${this.state.bagNumber(i)}</b>
            <span class="bagrow__w">${oz} oz &middot; $${price.toFixed(2)}</span>
            <span class="bagrow__st">${status}</span>
          </button>
          <button type="button" class="bagrow__open" data-bag-expand="${i}" aria-expanded="false" aria-controls="bagc-${i}" aria-label="Show what is in bag ${this.state.bagNumber(i)}">&#9662;</button>
          <button type="button" class="bagrow__del" data-bag-del="${i}" aria-label="Delete bag ${this.state.bagNumber(i)}">&times;</button>
          <ul class="bagrow__contents" id="bagc-${i}" hidden>${items}</ul>
        </li>`;
      })
      .join('');

    const clearBtn = this.querySelector('[data-bags-clear]');
    if (clearBtn) {
      const anyCandy = this.state.bags.some((b) => Object.keys(b.items).length > 0);
      clearBtn.hidden = !(anyCandy || sealedRows.length || this.state.bags.length > 1);
      clearBtn.dataset.confirm = '0';
      clearBtn.classList.remove('is-confirm');
      clearBtn.textContent = 'Clear all bags';
    }
  }

  _renderReceipt() {
    const listEl = this.querySelector('[data-mix-list]');
    const emptyEl = this.querySelector('[data-mix-empty]');
    if (!listEl) return;

    const bag = this.state.activeBag;
    const entries = Object.entries(bag.items);

    if (!entries.length) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    listEl.innerHTML = entries
      .map(([candyId, oz]) => {
        const candy = this.state.candyById[candyId];
        const price = candy ? candy.pricePerOz * oz : 0;
        return `<li data-mix-row="${candyId}" draggable="true">
          <span class="mix-row__name">${candy ? candy.name : candyId}</span>
          <span class="mix-row__oz">${oz} oz</span>
          <span class="mix-row__price">$${price.toFixed(2)}</span>
          <button type="button" class="mix-row__x" data-remove-oz="${candyId}" aria-label="Remove ${candy ? candy.name : ''} from this bag">&times;</button>
        </li>`;
      })
      .join('');
  }

  _openInfo(candyId) {
    const candy = this.state.candies.find((c) => String(c.id) === String(candyId));
    if (!candy || !(candy.ingredients || candy.flavor)) return;

    let dialog = this.querySelector('[data-info-dialog]');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'pym-info';
      dialog.setAttribute('data-info-dialog', '');
      dialog.setAttribute('aria-labelledby', 'pym-info-title');
      dialog.innerHTML =
        '<form method="dialog" class="pym-info__inner">' +
        '<h3 class="pym-info__title" id="pym-info-title"></h3>' +
        '<p class="pym-info__label" data-flavor-label>Flavor</p>' +
        '<p class="pym-info__text" data-flavor-text></p>' +
        '<p class="pym-info__label" data-info-label>Ingredients</p>' +
        '<p class="pym-info__text" data-info-text></p>' +
        '<button class="pym-info__close" value="close">Close</button>' +
        '</form>';
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close();
      });
      this.appendChild(dialog);
    }
    dialog.querySelector('.pym-info__title').textContent = candy.name;
    dialog.querySelector('[data-flavor-text]').textContent = candy.flavor || '';
    dialog.querySelectorAll('[data-flavor-label], [data-flavor-text]').forEach((n) => { n.hidden = !candy.flavor; });
    dialog.querySelector('[data-info-text]').textContent = candy.ingredients || '';
    dialog.querySelectorAll('[data-info-label], [data-info-text]').forEach((n) => { n.hidden = !candy.ingredients; });
    dialog.showModal();
  }

  _renderGrid() {
    const grid = this.querySelector('[data-candy-grid]');
    if (!grid) return;

    const bag = this.state.activeBag;
    const visible = this._sortCandies(this._filteredCandies());

    if (!this.state.candies.length) {
      grid.innerHTML = `<li class="flavgrid__empty">Add products to this collection to fill the candy wall.</li>`;
      return;
    }
    if (!visible.length) {
      grid.innerHTML = `<li class="flavgrid__empty">No candies match these filters.</li>`;
      return;
    }

    // Start with about a dozen in-stock picks (the popular ones first); "Show all" opens the rest.
    const SHOW = 12;
    const compact = !this.showAll && visible.length > SHOW + 2;
    let list = visible;
    if (compact) {
      const pop = new Set(this.popularHandles || []);
      const inStock = visible.filter((c) => c.inStock);
      list = (this.sortMode && this.sortMode !== 'popular') ? inStock.slice(0, SHOW) : [...inStock.filter((c) => pop.has(c.handle)), ...inStock.filter((c) => !pop.has(c.handle))].slice(0, SHOW);
    }

    grid.innerHTML = list
      .map((c) => {
        const ozInBag = bag.items[c.id] || 0;
        const imgSrc = c.cardImage || (c.resolvedImages && c.resolvedImages[0]);
        const infoBtn = (c.ingredients || c.flavor)
          ? `<button type="button" class="flav-card__info" data-info="${c.id}" aria-label="${c.flavor ? 'Flavor and ingredients' : 'Ingredients'} for ${c.name}" aria-haspopup="dialog">i</button>`
          : '';
        const media = imgSrc
          ? `<img class="flav-card__img" src="${imgSrc}" alt="" loading="lazy" width="80" height="80">`
          : `<span class="flav-card__img flav-card__img--placeholder" data-category="${c.category}" aria-hidden="true"></span>`;

        if (!c.inStock) {
          return `<li class="flav-card flav-card--soldout">
            ${infoBtn}
            ${media}
            <span class="flav-card__tag">Sold out</span>
            <span class="flav-card__name">${c.name}</span>
            <span class="flav-card__note">Sold out by the ounce</span>
          </li>`;
        }
        const popular = (this.popularHandles || []).includes(c.handle) ? '<span class="flav-card__popular">&#9733; Popular pick</span>' : '';
        const flash = c.flashPct > 0 ? `<span class="fd-corner" aria-hidden="true"><span class="fd-corner__t">Deal &minus;${c.flashPct}%</span></span>` : '';
        // the deal price rides on the category line so cards with a deal are no taller than the others
        const dealInline = c.flashPct > 0 ? ` <span class="flav-card__dealinline">$${c.pricePerOz.toFixed(2)}/oz <s>$${c.listPrice.toFixed(2)}</s></span>` : '';
        return `<li class="flav-card" data-card="${c.id}">
          ${flash}
          ${popular}
          ${infoBtn}
          ${media}
          <span class="flav-card__tag">${c.category}${dealInline}</span>
          <span class="flav-card__name">${c.name}</span>
          <span class="flav-card__controls">
            <button type="button" class="flav-card__step" data-remove-oz="${c.id}" ${ozInBag ? '' : 'disabled'} aria-label="Remove 1 oz of ${c.name}">&minus;</button>
            <span class="flav-card__oz">${ozInBag} oz</span>
            <button type="button" class="flav-card__step flav-card__step--add" data-add-oz="${c.id}" aria-label="Add 1 oz of ${c.name}">+1 oz</button>
          </span>
          <button type="button" class="flav-card__add4" data-add-oz4="${c.id}" aria-label="Add 4 oz (a quarter pound) of ${c.name}">+4 oz (&frac14; lb)</button>
        </li>`;
      })
      .join('');

    if (compact) {
      grid.insertAdjacentHTML('beforeend', `<li class="flavgrid__more"><button type="button" class="flavgrid__morebtn" data-show-all>Show all ${visible.length} candies</button></li>`);
    }
  }

  // "Surprise me": fills the bag with a random pick from what is on the wall right now (so the Sweet / Sour / vegan choices still count).
  // It adds enough to reach the first-bag minimum, or 4 oz if the minimum is already met. Fluffy (bulky) candy is left out so the bag never fills early.
  _surprise() {
    if (this._sealing) return;
    const max = window.MIX_BUILDER_CONSTANTS.BAG_MAX_OZ;
    const need = this.state.ozNeededForBag(this.state.activeBagIndex);
    const target = need > 0 ? need : 4;
    const pool = this._filteredCandies().filter((c) => c.inStock && !c.bulky);
    if (!pool.length) {
      this._flash('Nothing to pick from with these filters. Try Both.');
      return;
    }
    const order = pool.slice().sort(() => Math.random() - 0.5);
    const names = [];
    for (let i = 0; i < target; i++) {
      if (this.state.bagSpace(this.state.activeBagIndex) + 1 > max) break;
      const c = order[i % order.length];
      this.state.addOz(c.id);
      if (!names.includes(c.name)) names.push(c.name);
    }
    this.physics?.resetToBag(this.state.activeBag.items, window.MIX_BUILDER_CONSTANTS.PIECES_PER_OZ);
    this._haptic(10);
    if (this.game) this.game.check();
    this._flash('Surprise! We added ' + target + ' oz: ' + names.slice(0, 4).join(', ') + (names.length > 4 ? ' and more' : '') + '.');
  }
}

customElements.define('mix-builder', MixBuilder);
